import React, { useEffect, useRef, useState, useMemo } from "react";
import zeroTypingLogo from "../../Asset/ZEROTyingLogo.svg";
import { ReactSVG } from "react-svg";
import { NavLink, useNavigate } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import { toast } from "react-toastify";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { BsFillLightningChargeFill } from "react-icons/bs";
import SubscriptionPopUp from "../../component/subscription-popUp/subscriptionPopUp";
import { trackEvent } from "../../lib/mixpanel";
import PromoCodeField from "./PromoCodeField";

// Stripe returns money in minor units; the summary shows plain dollars.
const money = (minor) => (Number(minor || 0) / 100).toFixed(2);

const Checkout = ({
  planKey,
  checkoutToken = "",
  customerEmail = null,
  customerName = null,
  promo = null,
  onPromoApplied = () => {},
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  // The desktop app opens checkout in a browser that may not be signed in, so
  // the Redux user can be empty; fall back to the Stripe customer's details.
  const email = user?.EmailId || customerEmail || "";
  const holderName =
    `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim() ||
    customerName ||
    email;

  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Guards against double-submit

  // Normalize plan
  const plan = useMemo(() => {
    const key = String(planKey || "").toLowerCase().trim();

    const isAnnual =
      key === "annual" ||
      key.includes("pro_annual") ||
      key.includes("zt_pro_annual") ||
      key.includes("_annual");

    const pricePerMonthText = isAnnual ? "96.00" : "9.99";
    const totalDueTodayText = isAnnual ? "96.00" : "9.99";

    return {
      isAnnual,
      title: isAnnual ? "ZT Pro Annual" : "ZT Pro",
      displayPrice: isAnnual ? "96.00" : "9.99",
      intervalLabel: isAnnual ? "/year" : "/month",
      savingsText: "Save 20%",
      pricePerMonthText,
      totalDueTodayText,
      billingNote: isAnnual ? "Billed annually." : "Monthly plan.",
    };
  }, [planKey]);

  // Same shape on every checkout event so funnels can be built plan by plan.
  const planProps = useMemo(
    () => ({
      plan_type: plan.isAnnual ? "Pro_Annual" : "Pro",
      billing_cycle: plan.isAnnual ? "annual" : "monthly",
    }),
    [plan.isAnnual]
  );

  // checkout_started: the payment form is on screen. Once per mount — the
  // wrapper only renders this component after the session has loaded.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("checkout_started", planProps);
  }, [planProps]);

  // With a code applied the figures come from the rebuilt Stripe invoice;
  // otherwise they stay the plan's list price.
  const totals = useMemo(() => {
    const amounts = promo?.amounts;
    if (!amounts) {
      return {
        subtotal: plan.totalDueTodayText,
        discount: null,
        total: plan.totalDueTodayText,
      };
    }

    return {
      subtotal: money(amounts.subtotal),
      discount: amounts.discount > 0 ? money(amounts.discount) : null,
      total: money(amounts.total),
    };
  }, [promo, plan.totalDueTodayText]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    // Block re-entry: a payment is already being confirmed, or already succeeded.
    // Without this guard a double tap confirms the same PaymentIntent twice — the
    // second attempt fails with "A processing error occurred" even though the first
    // one already went through, showing a false error next to the success popup.
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: holderName,
              email,
            },
          },
        },
      });

      if (result.error) {
        toast.error(result.error.message);
        // Stripe's `type` is the class (card_error, validation_error, …);
        // `code`/`decline_code` say why (card_declined / insufficient_funds).
        trackEvent("checkout_failed", {
          ...planProps,
          error_type: result.error.type || "unknown",
          error_code: result.error.code || null,
          decline_code: result.error.decline_code || null,
          error_message: result.error.message || null,
        });
      } else if (result.paymentIntent && result.paymentIntent.status === "succeeded") {
        trackEvent("upgrade_completed", {
          ...planProps,
          payment_intent_status: result.paymentIntent.status,
        });
        // Payment successful - redirect to the success page.
        // The `fromCheckout` flag gates the success page so it can only be
        // reached right after a real payment, not by pasting the URL.
        navigate("/payment-successful", { state: { fromCheckout: true } });
      }
    } catch (err) {
      // confirmPayment itself threw (network drop, Stripe.js not loaded) —
      // previously an unhandled rejection with no feedback to the user.
      toast.error("Payment could not be processed. Please try again.");
      trackEvent("checkout_failed", {
        ...planProps,
        error_type: "exception",
        error_code: null,
        decline_code: null,
        error_message: String(err?.message || err),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-10 md:mx-[5%] md:py-10 p-4 ">
        <NavLink
          to={"/pricing"}
          className={`md:flex hidden gap-3 items-center text-[#33333370]`}
        >
          <HiArrowLeft className=" size-8" />
          <ReactSVG
            src={zeroTypingLogo}
            beforeInjection={(svg) => {
              svg.setAttribute("class", "w-auto h-10");
            }}
            className="w-auto"
          />
        </NavLink>

        <div className=" flex md:flex-row flex-col items-center justify-start md:items-start md:justify-between lg:gap-1 gap-5">
          {/* LEFT: Summary */}
          <div className="flex-1 w-full">
            {/* Desktop */}
            <div className="lg:w-[90%] w-full mr-auto xl:p-8 lg:p-6 p-4 rounded-xl bg-[#F9F9F9] hidden md:flex flex-col gap-6">
              <div className=" flex flex-col gap-2">
                <h2 className="text-[1.294rem] font-medium text-[#1A1A1A99] font-inter">
                  Subscribe to {plan.title}
                </h2>

                <div className="flex items-center gap-3">
                  <div className="text-[2.832rem] font-bold font-inter text-[#1A1A1AE5]">
                    ${plan.displayPrice}
                    <span className="text-[1.141rem] font-medium text-[#1A1A1A99]">
                      {plan.intervalLabel}
                    </span>
                  </div>

                  {plan.isAnnual && (
                    <span className="bg-green-100 text-green-700 text-sm font-semibold px-3 py-1 rounded-full">
                      {plan.savingsText}
                    </span>
                  )}
                </div>
              </div>

              <div className=" flex flex-col">
                <div className="flex justify-between font-inter mb-2">
                  <div>
                    <p className="font-medium text-[#1A1A1AE5] text-[1.141rem]">
                      {plan.title}
                    </p>
                    <p className=" text-[#1A1A1A80]">{plan.billingNote}</p>
                  </div>
                  <p className="text-[1.106rem] text-[#1A1A1AE5] font-medium">
                    ${plan.pricePerMonthText}
                  </p>
                </div>

                <div className="flex justify-between py-3 border-t-2 text-[1.124rem] text-[#1A1A1AE5] font-medium font-inter border-[#1A1A1A1A]">
                  <p>Subtotal</p>
                  <p>${totals.subtotal}</p>
                </div>

                {totals.discount && (
                  <div className="flex justify-between pb-3 text-[1.124rem] font-medium font-inter text-[#1A9E5F]">
                    <p>Discount{promo?.promoCode ? ` (${promo.promoCode})` : ""}</p>
                    <p>-${totals.discount}</p>
                  </div>
                )}

                <div className="border-t-2 border-[#1A1A1A1A] pt-3">
                  <PromoCodeField
                    planKey={planKey}
                    checkoutToken={checkoutToken}
                    promo={promo}
                    onApplied={onPromoApplied}
                  />
                </div>

                <div className="flex justify-between py-3 mt-3 border-t-2 border-[#1A1A1A1A] text-[1.124rem] text-[#1A1A1AE5] font-medium font-inter ">
                  <p>Total due today</p>
                  <p>${totals.total}</p>
                </div>
              </div>
            </div>

            {/* Mobile */}
            <div className=" md:hidden flex flex-col relative overflow-hidden gap-2 p-3 bg-[#EBF7FE] rounded-xl">
              <div className=" flex justify-between items-center gap-2">
                <NavLink
                  to={"/pricing"}
                  className={`flex gap-2 items-center text-[#33333370]`}
                >
                  <HiArrowLeft className=" size-4" />
                  <ReactSVG
                    src={zeroTypingLogo}
                    beforeInjection={(svg) => {
                      svg.setAttribute("class", "w-auto h-7");
                    }}
                    className="w-auto"
                  />
                </NavLink>

                <button
                  onClick={() => setIsOpen(true)}
                  className=" flex items-center justify-center gap-1 text-xs font-medium text-[#00000050]"
                >
                  <span className=" underline">Details</span>
                  <IoIosArrowDown />
                </button>
              </div>

              <div className=" flex flex-col items-center gap-4 pt-6 pb-4 font-inter">
                <div className="bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]  rounded-xl w-14 h-14 flex items-center justify-center   text-white">
                  <BsFillLightningChargeFill className="size-6" />
                </div>

                <span className=" text-lg font-medium text-center text-[#00000099]">
                  Subscribe to {plan.title}
                </span>

                <div className="flex flex-col items-center gap-2">
                  <div className="text-[2.832rem] font-bold font-inter text-[#1A1A1AE5]">
                    ${plan.displayPrice}
                    <span className="text-[1.141rem] font-medium text-[#1A1A1A99]">
                      {plan.intervalLabel}
                    </span>
                  </div>

                  {plan.isAnnual && (
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.savingsText}
                    </span>
                  )}
                </div>

                <span className="text-[15px] text-center text-[#00000099]">
                  Get access to unlimited words, AI commands, and early access
                  to new features with Zero Typing AI
                </span>
              </div>

              {/* Details sheet */}
              <div
                className={` absolute md:hidden inset-y-0 right-0 left-0 top-0 p-3  rounded-xl w-full h-full flex flex-col gap-2  bg-[#EBF7FE] z-50
                transition-transform duration-700  ease-in-out
                ${isOpen ? "-translate-y-0" : "-translate-y-[105%] "}`}
              >
                <div className=" flex justify-between items-center gap-2 font-inter">
                  <ReactSVG
                    src={zeroTypingLogo}
                    beforeInjection={(svg) => {
                      svg.setAttribute("class", "w-auto h-7");
                    }}
                    className="w-auto"
                  />

                  <button
                    onClick={() => setIsOpen(false)}
                    className=" flex items-center justify-center gap-1 text-xs font-medium text-[#00000050]"
                  >
                    <span className=" underline">Close</span>
                    <IoIosArrowUp />
                  </button>
                </div>

                <div className=" flex items-center justify-between mt-6">
                  <div className=" flex items-center gap-2">
                    <div className="bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]  rounded-lg w-7 h-7 flex items-center justify-center   text-white">
                      <BsFillLightningChargeFill className="size-4" />
                    </div>
                    <span className=" text-[15px] font-medium text-center text-[#000000]">
                      {plan.title}
                    </span>
                  </div>

                  <span>
                    <p className="text-[15px] text-[#000000E5] font-bold">
                      USD. ${plan.pricePerMonthText}
                    </p>
                  </span>
                </div>

                <div className=" flex items-start gap-2 mb-5">
                  <div className=" w-7 h-7 flex-shrink-0"></div>
                  <span className="flex-grow text-xs text-[#00000099]">
                    Get access to unlimited words, AI commands, and early access
                    to new features with Zero Typing AI
                    <br />
                    {plan.isAnnual ? "Billed annually." : ""}
                  </span>
                </div>

                <div className="flex justify-between pt-5 pb-3 border-t-[1.3px] text-[15px] text-[#1A1A1AE5] font-medium font-inter border-[#0000001A]">
                  <p>Subtotal</p>
                  <p className=" font-bold">${totals.subtotal}</p>
                </div>

                {totals.discount && (
                  <div className="flex justify-between pb-3 text-[15px] font-medium font-inter text-[#1A9E5F]">
                    <p>Discount{promo?.promoCode ? ` (${promo.promoCode})` : ""}</p>
                    <p className=" font-bold">-${totals.discount}</p>
                  </div>
                )}

                <div className="border-t-[1.3px] border-[#0000001A] py-4">
                  <PromoCodeField
                    planKey={planKey}
                    checkoutToken={checkoutToken}
                    promo={promo}
                    onApplied={onPromoApplied}
                    compact
                  />
                </div>

                <div className="flex justify-between py-4 border-t-[1.3px] border-[#0000001A] text-[15px] text-[#1A1A1AE5] font-medium font-inter ">
                  <p>Total due today</p>
                  <p className=" font-bold">${totals.total}</p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Payment */}
          <div className="flex-1 w-full">
            <div className="lg:w-[80%] w-full mx-auto flex flex-col gap-6 font-inter">
              <div className=" bg-[#F9FAFB] w-full border border-[#00000012] text-[#1A1A1AE5] md:text-[1.133rem] text-sm font-medium rounded-lg p-3 flex items-center gap-5">
                Email <span className=" font-normal">{email}</span>
              </div>

              <div className=" w-full  flex flex-col gap-2">
                <p className="font-medium md:text-[1.294rem] text-base text-[#1A1A1AE5]">
                  Payment method
                </p>
                <PaymentElement />
              </div>

              <button
                type="submit"
                onClick={handleSubmit}
                disabled={!stripe || isSubmitting}
                className=" w-full rounded-full border bg-transparent hover:border-[#3AB1F6] border-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]  hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="block w-full rounded-full  py-3 text-center font-inter text-[1.068rem] font-semibold  text-white ">
                  {isSubmitting ? "Processing…" : "Subscribe"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionPopUp isOpen={isSuccess} setIsOpen={setIsSuccess} />
    </>
  );
};

export default Checkout;
